// src/layouts/trafficmonitoring/routeFlows/RouteFlowBarDiagram.jsx
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Bar } from "react-chartjs-2";

/**
 * Presentational component for a bar chart inside an MUI Card.
 * It doesn't fetch data and knows no endpoints—it receives ready data/options/flags.
 *
 * Props:
 * - data: { labels, datasets } (Chart.js ready)
 * - options: object (Chart.js options)
 * - plugins: array (e.g., [ChartDataLabels])
 * - height: number|string (default: 350)
 * - loading: boolean
 * - error: string|null
 * - emptyText: string (default: "No Data Available")
 * - yMax: number|null (if set, overrides options.scales.y.max)
 */
function RouteFlowBarDiagram({
  data,
  options,
  plugins = [],
  height = 350,
  loading = false,
  error = null,
  emptyText = null,
  yMax = null,
}) {
  const { t } = useTranslation();
  const resolvedEmptyText = emptyText ?? t("trafficMonitoring.routeFlows.noDataAvailable");
  // Small helper: applies yMax without breaking the rest of the options
  const mergedOptions = useMemo(() => {
    if (yMax == null) return options || {};
    const base = options || {};
    const baseScales = base.scales || {};
    const baseY = baseScales.y || {};
    return {
      ...base,
      scales: {
        ...baseScales,
        y: { ...baseY, max: yMax, min: baseY.min ?? 0, beginAtZero: true },
      },
    };
  }, [options, yMax]);

  const hasData =
    data &&
    Array.isArray(data.labels) &&
    data.labels.length > 0 &&
    Array.isArray(data.datasets) &&
    data.datasets.some((ds) => Array.isArray(ds.data) && ds.data.length > 0);

  return (
    <Card
      sx={{
        mt: "0.5rem",
        p: "0.5rem",
        width: "100%",
        minHeight: typeof height === "number" ? `${height}px` : height,
      }}
    >
      <MDBox
        sx={{
          p: "0.5rem",
          height: typeof height === "number" ? `${height}px` : height,
          position: "relative",
        }}
      >
        {loading ? (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="h6">{t("trafficMonitoring.routeFlows.loading")}</MDTypography>
          </MDBox>
        ) : error ? (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="h6">{error}</MDTypography>
          </MDBox>
        ) : !hasData ? (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="h6">{resolvedEmptyText}</MDTypography>
          </MDBox>
        ) : (
          <Bar data={data} options={mergedOptions} plugins={plugins} />
        )}
      </MDBox>
    </Card>
  );
}

export default memo(RouteFlowBarDiagram);
